import { type FormEvent, memo, type ReactNode, useState } from "react";
import {
	Button, ButtonTypes, Checkbox, ColumnSortDirection, ConfirmModal, type Column, Icon, IconSize, Table,
	SettingsLayout, Card
} from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectCurrentUser } from "Data/Redux/Slices/User";
import "Components/ConfigLayout/User/UserManagement/UserManagement.less";
import UserManagementModal from "Components/ConfigLayout/User/UserManagement/UserManagementModal";
import type { User } from "Interfaces";
import { selectUserManagementSettings, setUserManagementAction } from "Data/Redux/Slices/Settings/User/Management";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { isEqual } from "lodash";
import { produce } from "immer";

const userManagementColumns: Column[] = [
	{
		dataIndex: "userId",
		title: "User ID",
		key: "userId",
		sortable: true,
		defaultSortOrder: ColumnSortDirection.SORT_ASC
	},
	{
		dataIndex: "group",
		title: "Group",
		key: "group"
	},
	{
		dataIndex: "email",
		title: "Email",
		key: "email"
	},
	{
		dataIndex: "notifications",
		title: "Notifications",
		key: "notifications",
		sortable: false
	},
	{
		dataIndex: "actions",
		title: "Actions",
		key: "actions",
		sortable: false
	}
];

function UserManagement() {
	const dispatch = useAppDispatch();
	const userManagementSettings = useAppSelector(selectUserManagementSettings);
	const [ localUserManagementSettings, setLocalUserManagementSettings ] = useState(userManagementSettings);
	const setUserManagementSettings = () => dispatch(setUserManagementAction(localUserManagementSettings));
	const disabled = isEqual(localUserManagementSettings, userManagementSettings);
	const loggedInUser = useAppSelector(selectCurrentUser);
	const { users: allUsers } = localUserManagementSettings;

	const handleUserUpdateSubmit = (index: number) => (newUser?: User) => {
		if (!newUser) {
			return;
		}

		return setLocalUserManagementSettings(produce(localUserManagementSettings, draft => {
			draft.users[ index ]= newUser;
		}));
	};

	const handleUserAddSubmit = (newUser?: User) => {
		if (!newUser) {
			return;
		}

		return setLocalUserManagementSettings(produce(localUserManagementSettings, draft => {
			draft.users.push(newUser);
		}));
	};

	const renderUserActions = (index: number, user?: User) => {
		let deleteContent: ReactNode;

		const deleteUser = () => {
			return setLocalUserManagementSettings(produce(localUserManagementSettings, draft => {
				draft.users = draft.users.filter(currentUser => currentUser.id !== user?.id);
			}));
		};

		if (user?.id !== loggedInUser?.id) {
			deleteContent = (
				<ConfirmModal
					okButton={ { text: "Confirm", onClick: deleteUser, type: ButtonTypes.TERTIARY } }
					cancelButton={ { text: "Cancel", type: ButtonTypes.DANGER } }
					title="Delete User"
					modalContent="Are you sure you want to do this? It cannot be undone."
				>
					<div className="table-icon-button" >
						<Icon name="trash" size={ IconSize.SMALLER } />
					</div>
				</ConfirmModal>
			);
		}

		return (
			<div className="table-actions">
				<UserManagementModal
					allUsers={ allUsers }
					user={ user }
					onSubmit={ handleUserUpdateSubmit(index) }
				>
					<div className="table-icon-button" >
						<Icon name="pencil" size={ IconSize.SMALLER } />
					</div>
				</UserManagementModal>
				{ deleteContent }
			</div>
		);
	};

	const tableData = allUsers?.map((user, index) => {
		const { id, userId, email, group, notifications } = user;

		return {
			key: id,
			userId,
			group,
			email,
			notifications: <Checkbox checked={ notifications } disabled />,
			actions: renderUserActions(index, user)
		};
	});

	const handleReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setLocalUserManagementSettings(userManagementSettings);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setUserManagementSettings();
	};

	return (
		<SettingsLayout.Form
			className="user-management-settings"
			onSubmit={ handleSubmit }
			onReset={ handleReset }
			noValidate
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="User Management">
					<UserManagementModal allUsers={ allUsers } onSubmit={ handleUserAddSubmit }>
						<Button
							className="add-user-button"
							icon={ { name: "plus" } }
							htmlType="button"
							type={ ButtonTypes.TERTIARY }
						>
							Add User
						</Button>
					</UserManagementModal>
					<Table
						columns={ userManagementColumns }
						data={ tableData }
					/>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(UserManagement);
